/**
 * Derived state — pure functions over AppState. The rail badge, the home
 * tile, and any future surface read the SAME selector, so they cannot
 * disagree (badge single-source, DESIGN.md §7.5).
 */
import type { AppState, JobState, QuoteOptions } from './state.js';
import {
  displayRouteLabel, formatRouteLabel, splitRouteLabel, type RouteEnds,
} from '../../shared/route-label.js';
import { MANUAL_SERVICES, type ManualServiceId } from '../../shared/service-schema.js';
// ⭐ Board 2089 M3 — the ONE normaliser, the ONE read-back formatter and the ONE
// refusal sentence. Nothing here parses a phone number itself.
import { formatPhoneWithCountry, parseCustomerPhone, phoneCountryName } from '../../shared/phone.js';
import { phoneRefusalCopy } from '../../shared/send-schema.js';
// ⛔ `formatPassengerCount` WAS IMPORTED HERE for the summary's count push
// (T1871 M6); T1749 M8 (R7) dropped that push — the exact count lives in the
// count input only now — so the import went with it. The formatter still lives
// in `shared/passengers.js` for the surfaces that DO render a count (the Bookings
// strip, the booking-detail Passengers row, the driver/customer messages).

/**
 * ⭐⭐ BOARD 2089 M3 (R-6, R-7) — THE ONE CUSTOMER-PHONE READ-BACK, DERIVED ONCE
 * FOR EVERY SCREEN THAT RENDERS THE NUMBER OFF THE JOB.
 *
 * Two surfaces read the phone out of job state — `components/CustomerInfoBlock`
 * (the inquiry-prefilled card) and `screens/Done` (the attribution line) — and
 * both had the same shape before this: `<a href={tel:raw}>{raw}</a>`. A second
 * copy of the parse-then-format decision is how one screen ends up dialling a
 * string the other has already normalised.
 *
 * ⛔ THE HREF AND THE TEXT ARE DIFFERENT ON PURPOSE. A `tel:` target carries the
 * BARE E.164 whenever one is known, because that is what a dialler and a send
 * both use; the text carries the spaced international form and the country name,
 * because that is what Helen reads aloud and what makes a `+44` booking legible
 * as a visitor rather than as a typo. When nothing parses, BOTH stay the raw
 * typed string exactly as they were — the customer said it, and a card headed
 * "From the customer" may not silently drop it.
 *
 * ⛔⛔ `server` IS THE SERVER-OF-RECORD PHONE, PASSED AS THE PAIR IT IS — `known`
 * AND `e164` — AND NEVER COLLAPSED TO ONE NULLABLE STRING. Board 2089 M5 (seat
 * drive) measured why: Done used to pass `serverPhoneKnown ? serverPhone : null`,
 * which gives "the read has not landed" and "the read LANDED and says no number"
 * the same spelling, so a landed null fell through to parsing what Helen TYPED and
 * rendered a working `tel:` link directly above the send block's "no saved mobile
 * number". That is T651, re-opened: a claim from client state above a refusal from
 * server truth. The rule is now `SendQuote`'s `targetOf` rule — nothing is a
 * number of record until the record has LANDED, and once it has, the record
 * DECIDES, null included:
 *   - landed, with an E.164  -> that number, formatted, as a link;
 *   - landed, with NO number -> the typed string as plain TEXT, `href: null`, so
 *     no renderer can offer a dial action the send block refuses;
 *   - not landed (no `server`, or `known: false`) -> the typed pair is parsed,
 *     exactly as before. `CustomerInfoBlock` (a pre-save, inquiry-prefilled card)
 *     and a not-yet-saved walk-in have no server answer and need this path.
 *
 * ⚠️ `refusal` is non-null ONLY when there IS a typed number and it does not
 * parse. An empty box is not a refusal, and a parsed number has nothing to
 * explain.
 *
 * simplified: one formatter at the three customer read-backs only (this
 * selector's two callers plus `screens/BookingDetail.tsx`, which holds the
 * stored E.164 rather than a job and calls `formatPhoneWithCountry` directly).
 * Every OTHER bare-E.164 render in the kiosk — `TotalBar`, `LeadContext`,
 * `SendQuote` — is left exactly as it was. Upgrade trigger: a fourth screen
 * Helen-tested against this rendering.
 */
export type CustomerPhoneRead = {
  /** The `tel:` target: the E.164 when one is known, else the raw string. NULL
   *  when the server of record has LANDED with no number: render `text` as plain
   *  text, never as a link (a `tel:` the send block refuses is T651). */
  href: string | null;
  /** What the reader sees: `+353 85 123 4567 (Ireland)`, else the raw string. */
  text: string;
  /** Why no text can be sent, or null. */
  refusal: string | null;
};

/** The server-of-record phone as a job carries it after the booking-detail read:
 *  `known` says the read has LANDED, `e164` is what it found (null = none). */
export type ServerPhoneOfRecord = { known: boolean; e164: string | null };

export function customerPhoneRead(j: JobState, server?: ServerPhoneOfRecord): CustomerPhoneRead {
  const raw = j.customerPhone;
  if (server?.known === true) {
    const e164 = server.e164 != null && server.e164 !== '' ? server.e164 : null;
    if (e164 !== null) return { href: e164, text: formatPhoneWithCountry(e164), refusal: null };
    // ⛔ LANDED, AND THE RECORD SAYS NO NUMBER. Never fall back to parsing the
    // typed string here: a valid parse would print `(Ireland)` and a working link
    // for a number the server did not store. The raw string is still shown - it is
    // what the customer said - but as text. `refusal` stays null because the one
    // caller with a landed record (Done) has a send block that already states the
    // condition in its own words, and `phoneRefusalCopy` ("not recognised as a …
    // number") would be FALSE about a typed number that parses.
    return { href: null, text: raw, refusal: null };
  }
  const parsed = parseCustomerPhone(raw, j.customerPhoneCountry);
  if (parsed.ok) {
    return { href: parsed.e164, text: formatPhoneWithCountry(parsed.e164), refusal: null };
  }
  // `parsed.country` is the region the parse was ATTEMPTED under - the job's
  // chosen country, or the Ireland default when it names none - so the sentence
  // can always say which rule the number failed.
  return {
    href: raw,
    text: raw,
    refusal: parsed.reason === 'empty' ? null : phoneRefusalCopy(phoneCountryName(parsed.country)),
  };
}

export type BadgeView =
  | { kind: 'count'; text: string }
  | { kind: 'error'; text: '!' }
  | null;

/**
 * The New-inquiries badge, derived from the one state source. Error renders
 * as a DISTINCT '!' badge — never hidden, never a silent zero (the badge
 * must never understate; a fetch failure is not "no inquiries").
 */
export function inboxBadge(state: AppState): BadgeView {
  if (state.inboxCountError) return { kind: 'error', text: '!' };
  if (state.inboxCount === null || state.inboxCount === 0) return null;
  return { kind: 'count', text: String(state.inboxCount) };
}

/** The Price-changes rail badge — same distinct-error rule (a failed
 *  reconcile read renders '!', never "all up to date"; R13 HIGH class). */
export function rateChangesBadge(state: AppState): BadgeView {
  if (state.rateChangesError) return { kind: 'error', text: '!' };
  if (state.rateChangesCount === null || state.rateChangesCount === 0) return null;
  return { kind: 'count', text: String(state.rateChangesCount) };
}

/**
 * The Nursing-home-requests rail badge (T928 slice 1) — the third badge, same
 * rule as the two above: an error is a DISTINCT '!', never a silent zero.
 *
 * ⛔ IT COUNTS WHAT WAITS ON HER, NOT the list length. The inbox also carries
 * priced, confirmed, declined and cancelled rows, and a badge over all of them
 * would never reach zero — a permanent number reads as broken and gets ignored,
 * which is worse than no badge at all. What Helen needs to see is what is still
 * waiting on her. ⭐ Board 2814 M2 (DD-11): that is the Needs you now length,
 * `needsHelenNow` in `lib/actions.ts`, the one table the count, the tab label and
 * the Requests screen's lists all read (a `null` crossings read is the '!' mark).
 */
export function portalBadge(state: AppState): BadgeView {
  if (state.portalCountError) return { kind: 'error', text: '!' };
  if (state.portalCount === null || state.portalCount === 0) return null;
  return { kind: 'count', text: String(state.portalCount) };
}

/**
 * Edited-badge truth: has Helen changed a flag away from the customer's
 * ORIGINAL statement? Compared against the immutable `customerFlags`
 * baseline (never her last save). Walk-ins (null baseline) show no badge.
 */
export function editedFlags(state: AppState): (keyof QuoteOptions)[] {
  const baseline = state.job.customerFlags;
  if (baseline === null) return [];
  const edited: (keyof QuoteOptions)[] = [];
  for (const key of Object.keys(baseline) as (keyof QuoteOptions)[]) {
    const original = baseline[key];
    if (original === undefined) continue;
    if (state.job.options[key] !== original) edited.push(key);
  }
  return edited;
}

/**
 * Is a quote request in flight? Pure derivation from the two counters the
 * store already carries — no new state.
 *
 * This is the money guard. `job.band` updates SYNCHRONOUSLY on a pick, but
 * `job.quote` only lands when the fetch resolves, so between the two there is a
 * window where the job holds a NEW band next to the PREVIOUS band's price. A
 * save in that window would persist a band/price pair the engine never computed
 * together. `persistCurrentQuote` refuses to write while this is true, and the
 * save actions render disabled.
 *
 * A settled state is req === applied: `resetForNewQuote` moves both, and
 * `applyQuoteResult` catches applied up to the latest request (including on a
 * FAILED fetch, which applies a null quote — so a failure settles cleanly).
 */
export function quotePending(state: AppState): boolean {
  return state.quoteRequestSeq !== state.appliedQuoteSeq;
}

/** Error copy for the CURRENT job's booking only (§7.3 scoping). */
export function currentBookingError(state: AppState): string | null {
  const id = state.job.bookingId;
  if (id === null) return null;
  return state.bookingErrors[id] ?? null;
}

/**
 * The §6 journey restatement: customer · route · date, time · band · extras.
 * Pure derivation from the job; label lookups are the caller's (registry).
 * Skips whatever is unknown, never renders placeholders.
 */
/**
 * The place a precedent lookup keys on (T373 M5), or null if there is none.
 *
 * Only journeys WITHOUT a grid price have a "place" worth citing prior quotes
 * for: an off-gazetteer end (the Howth case — the substitution motive) or an
 * estimate's anchored place. A plain grid price needs no precedent — it has a
 * real rate. Drop-off wins over pickup (the destination is what an out-of-area
 * job is usually about).
 */
export function precedentPlace(job: JobState): string | null {
  if (job.dropoffOffGazetteer && job.toLocationLabel) return job.toLocationLabel;
  if (job.pickupOffGazetteer && job.locationLabel) return job.locationLabel;
  if (job.quote?.status === 'estimated' && job.quote.anchoredPlace) return job.quote.anchoredPlace;
  return null;
}

/**
 * T515 — the journey's PICKUP end, as a place string.
 *
 * The one place the three-way fallback lives, so persist (journeyStops), the
 * team-alert payload (getPreview) and both display surfaces can never drift.
 * `pickupTerminal` wins because an ARRIVALS pickup is a terminal while the
 * PRICED town sits on the address end — without it, locationLabel (the priced
 * town) renders as the pickup and the airport vanishes from the job sheet.
 *
 * Do NOT collapse this to `pickupSearchText ?? locationLabel`: the two are not
 * mutually exclusive after `setPickup` (actions.ts), which sets the label and
 * leaves the search text, so an unresolved-then-picked lead would regress to
 * rendering its raw composed text.
 *
 * T540 (516) — a FOURTH, last-resort term for hand-priced services. A wedding
 * never visits the Location screen (manual pricing routes Home straight to
 * Passengers), so the first three terms are all null and `journeyStops` emitted
 * NOTHING: the pickup street and Eircode Helen typed on the Quote screen were
 * silently discarded, while the prompt that asked for them disappeared as she
 * typed. For a manual job with no gazetteer place, the typed street IS the
 * pickup end. Last in the chain, and gated on MANUAL_SERVICES, so every service
 * that resolves a place (airport, golf, local-other) is inert BY CONSTRUCTION,
 * not by guard analysis.
 *
 * Provenance caveat (T540 D3, ruled deliberately): CustomerInfoBlock renders
 * this under a "From the customer" heading, so on a manual job it can surface
 * text the OPERATOR typed. Accepted rather than split into a second selector:
 * on any reopened booking the THIRD term already returns the operator's street
 * (reopen maps stops[0].address into pickupSearchText), so a second selector
 * would defer the misattribution by one save, not prevent it — while costing
 * the single-source invariant this function exists to hold.
 */
export function pickupEndPlace(j: JobState): string | null {
  // T906 S1 — a golf job now STATES its pickup, so the stated place outranks
  // the composite label it used to be read out of. Ordered above locationLabel
  // rather than below it because on a REVERSED on-list pair the two disagree:
  // the composite names the rate row's direction, this names the journey's.
  return (
    j.golfPickupPlace ??
    j.pickupTerminal ??
    j.locationLabel ??
    j.pickupSearchText ??
    (MANUAL_SERVICES.includes(j.service as ManualServiceId) ? j.pickupAddressLine.trim() || null : null)
  );
}

/**
 * T906 S1 — does this job carry EXPLICIT golf ends, and what are they?
 *
 * The ONE predicate for "the golf picker stated both ends", exported for the
 * same single-source reason as `golfCourseEnds` below: it decides BOTH what
 * `journeyStops` persists and whether `DriverDetails` offers a drop-off
 * capture, and those two must never be able to disagree. A UI that invites a
 * capture the builder then discards is exactly the defect this project exists
 * to remove.
 *
 * null unless BOTH ends are present — a half-stated journey has no pair.
 */
export function golfStatedEnds(j: JobState): { origin: string; destination: string } | null {
  if (j.service !== 'golf') return null;
  const origin = j.golfPickupPlace;
  const destination = j.golfDestinationPlace;
  return origin !== null && destination !== null ? { origin, destination } : null;
}

/**
 * T540 (516) — the journey's DROP-OFF end, the mirror of pickupEndPlace, and
 * the single source for the same three consumers.
 *
 * For a hand-priced service the VENUE is the destination: a wedding books by
 * venue and has no drop-off picker at all. Without this, giving a wedding a
 * pickup stop actively made things worse in two ways:
 *
 *   1. `journeySummary` builds its route from job state, so a lone pickup made
 *      `[street, null].filter(Boolean)` truthy and killed the `|| j.venue`
 *      fallback — deleting the venue from the Quote and Done summaries, which
 *      on Done is the ONLY place it appears.
 *   2. Both deposit alerts read `dropoff: (last ?? first)?.address`, so a
 *      SINGLE stop renders as the destination. The customer's pickup street
 *      would have reached them labelled as where the bus is going.
 *
 * `venue` deliberately outranks `dropoffSearchText`: VenueControl stays mounted
 * on a reopened wedding by design, while dropoffSearchText carries the PERSISTED
 * venue from the stop row. Ordering the carry first would let a stale venue win
 * over Helen's correction in exactly the rows the deposit alerts read.
 *
 * Non-manual services keep today's expression byte-for-byte.
 */
export function dropoffEndPlace(j: JobState): string | null {
  // T906 S1 — the golf destination, stated by the picker. Before it, golf had
  // no drop-off end at all here: neither `toLocationLabel` nor
  // `dropoffSearchText` is ever written by a golf pick, so this returned null
  // and `journeyStops` persisted the job with NO destination row.
  if (j.golfDestinationPlace !== null) return j.golfDestinationPlace;
  return MANUAL_SERVICES.includes(j.service as ManualServiceId)
    ? j.venue?.trim() || j.toLocationLabel || j.dropoffSearchText || null
    : (j.toLocationLabel ?? j.dropoffSearchText);
}

/**
 * T660 M6 (R1) — the pickup end as an OPERATOR SCREEN renders it, and the ONE
 * place that decides which of the two renderings a given value gets.
 *
 * `displayRouteLabel` collapses EVERY hub boundary, and `pickupEndPlace` does
 * not always return a route label: its last two terms are `pickupSearchText`
 * (the customer's own inquiry words) and `pickupAddressLine` (a street Helen
 * typed). Applied blind it rewrote those too — 'The K Club — Straffan' rendered
 * as 'The K Club → Straffan' under a heading that says "From the customer",
 * which is exactly the hazard test/shared/route-label.spec.ts's "a lone dash in
 * an operator typed address must survive verbatim" invariant exists to prevent.
 * No BYTE test can separate the two: a typed 'X — Y' and a stored route label
 * are the same shape, and `splitRouteLabel` reads both as a clean pair. So the
 * discriminator is the JOB, not the string, and all three conjuncts are load
 * bearing:
 *   - `service === 'golf'` — the hub boundary is a golf-rates construct. Every
 *     golf label is composed `${hubName} — ${routeName}` (shared/pricing-flatten
 *     for the seeded grid, core/data/audit-repo's entryKeys for an operator-added
 *     route); no other service mints one.
 *   - `locationId !== null` — the label came from a PICKED route, not from
 *     `setPickupOffGazetteer`, which writes free text into `locationLabel` with a
 *     null id. (Golf's Location variant offers no escape today, so this is a
 *     guard against the next flow, not a live path.)
 *   - `end === j.locationLabel` — the VALUE being rendered is that label, rather
 *     than a terminal or a typed fallback that outranked it.
 * A golf job with no route picked therefore renders its typed pickup verbatim,
 * which is pinned in test/client/operator-em-dash.spec.tsx.
 *
 * Everything else gets `formatRouteLabel`, the pre-M6 rendering, which touches
 * only the em-dash + arrow PAIR and so cannot corrupt a lone dash.
 *
 * Lives here beside `pickupEndPlace` for the same single-source reason that
 * selector does: its three readers (journeySummary below, CustomerInfoBlock and
 * the team-alert payload in lib/actions) must never disagree about how one
 * route renders.
 */
export function pickupEndDisplay(j: JobState): string | null {
  const end = pickupEndPlace(j);
  return j.service === 'golf' && j.locationId !== null && end === j.locationLabel
    ? displayRouteLabel(end)
    : formatRouteLabel(end);
}

/**
 * T608 M6a — is THIS job a golf COURSE leg, and if so what are its two ends?
 *
 * The ONE expression of that question, for the same single-source reason as the
 * two selectors above: it decides BOTH what gets persisted and what gets
 * rendered, and those two must never be able to disagree.
 *
 *   - `journeyStops` (client/lib/actions.ts) records the hub and the course as
 *     two stops and writes the course row with NO street data
 *     (`addressLine`/`eircode`/`town` all null) — a course has no street, and
 *     those columns describe where the party was collected, which is the hub row.
 *   - `DriverDetails` / `DriverDetailsLines` (client/components/DriverDetails.tsx)
 *     therefore render NO drop-off capture and NO drop-off echo line here. The
 *     UX BLOCK this fixes: Helen typed 'Clubhouse entrance, side gate' into a
 *     live Drop-off address field, the Quote screen echoed it back, and the save
 *     dropped it with no trace — the exact class of defect this project exists to
 *     remove. Hiding the two dead inputs is the fix; persisting a course-side
 *     street is a different (out-of-scope) design.
 *
 * Written as ONE shared function rather than two agreeing conditions because
 * that is how this defect returns: if either copy drifted, the UI would hide
 * fields that WOULD have persisted, or invite a capture that will not.
 *
 * null for every non-course shape, and callers must degrade to their generic
 * per-end behaviour on null rather than invent an end: a non-golf service, a
 * golf AIRPORT leg (3 of the 21 seeded routes, whose route name carries no
 * leading arrow — see RouteEnds.courseLeg), a legacy/hand-typed golf label with
 * no hub boundary, and a golf job with no route label at all.
 */
export function golfCourseEnds(j: JobState): RouteEnds | null {
  if (j.service !== 'golf') return null;
  const ends = splitRouteLabel(j.locationLabel);
  return ends?.courseLeg === true ? ends : null;
}

export function journeySummary(
  state: AppState,
  labels: { serviceLabel: string | null; addOnLabels: Readonly<Record<string, string>> },
): string {
  const j = state.job;
  const parts: string[] = [];
  if (j.customerName) parts.push(j.customerName);
  // §6: never truncate the route — each end independently falls back to the
  // raw inquiry text when unresolved (same rule as CustomerInfoBlock).
  // pickupEndDisplay: a golf leg's stored label carries a hub boundary (an em
  // dash, plus an arrow on a COURSE leg); on a picked golf route every boundary
  // renders as one arrow, and anything else renders verbatim (see the selector).
  // This selector feeds Quote, Done and the lead strip (App.tsx renders the
  // returned string and nothing persists it), so without it the SAME route
  // rendered two different ways on one screen.
  const pickupDisplay = pickupEndDisplay(j);
  // The DROP-OFF end never carries a hub boundary: a golf route label lands in
  // `locationLabel` (the pickup end) and this end reads venue / toLocationLabel /
  // dropoffSearchText, none of which a golf pick writes. So it keeps the pre-M6
  // rendering, which cannot corrupt a lone dash in a typed place.
  const dropoffDisplay = formatRouteLabel(dropoffEndPlace(j));
  const route = [pickupDisplay, dropoffDisplay].filter(Boolean).join(' to ') || j.venue || null;
  if (route) parts.push(route);
  else if (labels.serviceLabel) parts.push(labels.serviceLabel);
  const when = [j.journeyDate, j.journeyTime].filter(Boolean).join(', ');
  if (when) parts.push(when);
  // ⛔⛔ T1749 M8 (R7, §6) — THE BAND AND THE EXACT COUNT NO LONGER JOIN THIS
  // LINE. They used to (`if (j.band) parts.push(j.band)` and a
  // `formatPassengerCount` push, T1871 M6), which put the band and "20
  // passengers" on the summary AS WELL AS on the band chips and the count input —
  // three renders of one fact on Quote. R7's ruling is that the band lives on the
  // CHIPS only and the count in the COUNT INPUT only; the summary is the journey,
  // not the party. `passengers-exact-render.spec.tsx` counts occurrences on the
  // rendered Quote (exactly one band, one count) and Done (zero of each), which
  // is the guard that keeps this line from re-acquiring either.
  // ⚠️ THE SCREEN SUMMARY ONLY. This is `selectors.ts`'s `journeySummary`, which
  // feeds Quote, Done and the lead strip; the CUSTOMER message has its OWN
  // `journeySummary` in `notify/customer-message.ts` (a different function, same
  // name) and is untouched — the band still reaches the customer there.
  // ⚠️ AND THE OVER-BAND FOLD MOVED WITH IT: the count-on-this-line used to pull
  // the M5 mismatch into the fold; that job is now the State B banner's, graded
  // in the fold by `e2e/tests/known-bugs/bug-g-over-band-fold.spec.ts`.
  const extras = journeyExtras(state, labels);
  if (extras !== '') parts.push(extras);
  return parts.join(' · ');
}

/**
 * ⭐⭐ T1749 M8 round 3 (ux round 2's rung 2) — THE EXTRAS, ON THEIR OWN, because
 * Done renders them as a caption line rather than as the tail of the sentence.
 *
 * ⛔ WHY THE SPLIT EXISTS AT ALL, and it is a FOLD measurement rather than a
 * preference: with five requirements toggled the journey line WRAPS to two lines
 * at 1024, which pushed Done's gold primary to 703.3 against the ~700 plane the
 * milestone grades. As their own caption line the same five facts cost one tight
 * line instead of a wrapped sentence, and they read as a list, which is what they
 * are.
 * ⛔ ONE SOURCE: `journeySummary` above COMPOSES this, so the sentence and the
 * caption can never disagree about which extras a job carries. Quote and the lead
 * strip keep the composed sentence untouched — nothing about them moves.
 */
export function journeyExtras(
  state: AppState,
  labels: { addOnLabels: Readonly<Record<string, string>> },
): string {
  const j = state.job;
  return (Object.keys(j.options) as (keyof QuoteOptions)[])
    .filter((k) => (typeof j.options[k] === 'number' ? (j.options[k] as number) > 0 : j.options[k]))
    .map((k) => labels.addOnLabels[k] ?? k)
    .join(', ');
}

/** The journey line WITHOUT its extras — Done's own head line (see
 *  `journeyExtras`). Composed from the same function the full sentence uses, so
 *  the two cannot drift. */
export function journeyLineWithoutExtras(
  state: AppState,
  labels: { serviceLabel: string | null; addOnLabels: Readonly<Record<string, string>> },
): string {
  const extras = journeyExtras(state, labels);
  const full = journeySummary(state, labels);
  if (extras === '') return full;
  const tail = ` · ${extras}`;
  return full.endsWith(tail) ? full.slice(0, -tail.length) : full;
}

/**
 * K5-7 (DESIGN.md §6) — the restatement, re-headed with the name Helen has
 * TYPED but not yet committed.
 *
 * ⛔ WHY THIS IS NOT A BRANCH INSIDE `journeySummary`, which is where the plan
 * put it: THE DRAFT NAME IS NOT IN THE STORE. `QuoteScreenProps.onSetCustomer`
 * lifts name/phone at the FOUR commit call sites deliberately (accept /
 * saveOverride / park / recordAgreed; its own comment says a fifth field lifted
 * at three would silently drop on the fourth), so `state.job.customerName` is
 * still '' while Helen is typing and a store selector has nothing to read. The
 * draft lives in `screens/Quote.tsx`'s local state and nowhere else.
 *
 * So this takes the string `journeySummary` already produced and swaps its
 * HEAD. Every other reader (Done, the lead strip) keeps the committed name,
 * which is the right answer there: by then the name IS committed.
 *
 * ⚠️ It swaps, never prepends, when a committed name is already leading, so the
 * card can never read "Aoife · Aoife Byrne · Bray to ...". If the committed name
 * is present but is NOT the head (it cannot be today, and a future reordering of
 * §6 would make it so) the summary is returned untouched rather than mangled.
 */
export function summaryWithDraftName(
  summary: string,
  draftName: string,
  committedName: string,
): string {
  const draft = draftName.trim();
  if (draft === '' || draft === committedName) return summary;
  if (committedName !== '') {
    if (summary === committedName) return draft;
    if (summary.startsWith(`${committedName} · `)) return `${draft}${summary.slice(committedName.length)}`;
    return summary;
  }
  return summary === '' ? draft : `${draft} · ${summary}`;
}
